<template>
  <div class="scroll full-height bg-white">
    <!-- ON FIELD -->
    <q-list separator>
      <q-item-label header class="text-weight-bold text-primary bg-grey-2"
        >On Field ({{ onFieldPlayers.length }})</q-item-label
      >
      <q-item
        v-for="entry in onFieldPlayers"
        :key="'field-' + entry.player.id"
        clickable
        v-ripple
        :active="selectedPlayerIds.includes(entry.player.id!)"
        active-class="bg-blue-1 text-weight-bold"
        @click="emit('toggle-selection', entry.player.id!)"
      >
        <q-item-section avatar>
          <q-avatar color="primary" text-color="white" size="sm">
            {{ getPositionByPlayerId(entry.player.id!) }}
          </q-avatar>
        </q-item-section>
        <q-item-section>
          <q-item-label
            >#{{ entry.player.uniformNumber || '-' }} {{ entry.player.name }}</q-item-label
          >
        </q-item-section>
      </q-item>
    </q-list>

    <!-- BENCH -->
    <q-list separator>
      <q-item-label header class="text-weight-bold text-grey-8 bg-grey-2"
        >Bench ({{ benchPlayers.length }})</q-item-label
      >
      <q-item
        v-for="entry in benchPlayers"
        :key="'bench-' + entry.player.id"
        clickable
        v-ripple
        :active="selectedPlayerIds.includes(entry.player.id!)"
        active-class="bg-blue-1 text-weight-bold"
        @click="emit('toggle-selection', entry.player.id!)"
      >
        <q-item-section avatar>
          <q-avatar color="grey-4" text-color="grey-8" size="sm">B</q-avatar>
        </q-item-section>
        <q-item-section>
          <q-item-label
            >#{{ entry.player.uniformNumber || '-' }} {{ entry.player.name }}</q-item-label
          >
        </q-item-section>
      </q-item>
    </q-list>
  </div>
</template>
<script setup lang="ts">
import { computed } from 'vue'
import type { DefenseSnapshot, LineupEntry } from 'stores/base-types'

const props = defineProps<{
  defenseSnapshot: DefenseSnapshot
  lineup: LineupEntry[]
  selectedPlayerIds: number[]
}>()

const emit = defineEmits<{ (e: 'toggle-selection', playerId: number): void }>()

const getPositionByPlayerId = (playerId: number): string => {
  for (const [pos, state] of Object.entries(props.defenseSnapshot)) {
    // Safely extract the ID supporting both legacy numbers and the new object format
    const pId = typeof state === 'number' ? state : state?.playerId
    if (pId === playerId) return pos // Returns '1' through '9'
  }
  return '-'
}

const allRosterEntries = computed(() => {
  const roster = props.lineup.concat(props.lineup.flatMap((l) => l.substitutions || []))

  return roster.sort((a, b) => {
    // Treat falsy/missing positions as 99 so they sort to the bottom
    const posA = a.startingPosition || 99
    const posB = b.startingPosition || 99
    return posA - posB
  })
})

// 1. Extract just the player IDs from the snapshot values
const activePlayerIds = computed(() => {
  return Object.values(props.defenseSnapshot).map((state) =>
    typeof state === 'number' ? state : state?.playerId,
  )
})

const onFieldPlayers = computed(() => {
  // 2. Filter the roster against that array of IDs
  return allRosterEntries.value.filter((entry) => activePlayerIds.value.includes(entry.player.id!))
})

const benchPlayers = computed(() => {
  // FIX: Added the '!' to negate the check, returning only players NOT on the field
  return allRosterEntries.value.filter((entry) => !activePlayerIds.value.includes(entry.player.id!))
})
</script>
